

























































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, ProvideReactive, Watch } from 'vue-property-decorator';

// Store types
import { ToolSystem, ToolUnit, SystemSplit } from '@/store/store.types';

// Shared types
import { Brand, System as ISystem, IndoorUnit, IndoorUnitColor, OutdoorUnit, OutdoorUnitColor } from '@coldtec/cos-types';

// Internal system components
import SystemHeading from './SystemHeading.vue';
import SystemSpecsLink from './SystemSpecsLink.vue';
import SystemImages, { SystemImage } from './SystemImages.vue';
import SystemSpecsPreview from './SystemSpecsPreview.vue';
import SystemAccordion, { Section } from './accordion/SystemAccordion.vue';

/**
 *  Component that represents a system tile, including his
 *  summary, images, preview specs, price, including units,
 *  description, accessories and actions to open his
 *  specs and toggle his selected state.
 */
@Component({
    components: {
        SystemHeading,
        SystemSpecsLink,
        SystemImages,
        SystemSpecsPreview,
        SystemAccordion,
    },
})
export default class System extends Vue {

    public $refs!: {
        accordion: SystemAccordion;
    };

    @Prop({ type: Object, required: true })
    @ProvideReactive('system')
    private system!: ISystem;

    @Prop({ type: Object, required: true })
    @ProvideReactive('toolSystem')
    private toolSystem!: ToolSystem;

    /**
     *  Holding the highlighting lock state.
     */
    private lockHighlighting = false;

    /**
     *  Holding the images of all the system's including units.
     */
    private images: Array<SystemImage> = [];

    /**
     *  Used to get the title for the button to select or deselect
     *  this system, interpolated with the name of the system.
     */
    private get selectTitle(): string {

        return `${ this.toolSystem.selected ? 'Deselecteer' : 'Selecteer' } de ${ (this.system.brand as Brand).name } ${ this.system.sku } `;

    }

    /**
     *  Construct the images Array for the system, including
     *  every image of every color of every including unit.
     */
    private created(): void {

        for (const units of [ 'indoor_units', 'outdoor_units' ] as Array<keyof ISystem>) {

            for (const unit of (this.system[units] || []) as Array<IndoorUnit> | Array<OutdoorUnit>) {

                for (const color of (unit.colors || []) as Array<IndoorUnitColor> | Array<OutdoorUnitColor>) {

                    for (const image of color.images) {

                        this.images.push({
                            title: image.name,
                            src: image.file,
                        });

                    }

                }

            }

        }

    }

    /**
     *
     *  Lock the automatic highlighting of the units or accessories when
     *  mounted and scroll the system into view if already selected.
     *
     *  (i) This ensures a system is scrolled into view when a users wants
     *  to edit the system (or add an accessory) from the overview
     *
     */
    private mounted(): void {

        this.lockHighlighting = true;

        // Give the syncing of the request spaces / spaces some time
        setTimeout(() => {

            if (this.toolSystem.selected) {

                this.$el.scrollIntoView({
                    behavior: 'smooth',
                });

            }

            this.lockHighlighting = false;

        }, 200);

    }

    /**
     *  Used to open and highlight the including units
     *  or accessories in case a system got selected.
     */
    @Watch('toolSystem.selected')
    private _selectedUpdate(selected: boolean): void {

        if (this.lockHighlighting || !selected) {

            return;

        }

        /**
         *  Open the accessories in case of single
         *  split and the units in case of a multi.
         */
        let openSection: Section | null = null;

        switch (this.toolSystem.split) {

            case SystemSplit.Single:

                openSection = Section.Accessories;

                break;

            case SystemSplit.Multi:

                openSection = Section.Units;

                break;

        }

        if (!openSection) {

            return;

        }

        this.$refs.accordion.openAndHighlight(openSection);

    }

    /**
     *  Used to open the accessories in case the user
     *  selected a space for all inside units.
     */
    @Watch('toolSystem.indoorUnits', { deep: true })
    private _indoorUnitsUpdate(indoorUnits: Array<ToolUnit>): void {

        if (this.lockHighlighting) {

            return;

        }

        let openAccessories = true;

        for (const unit of indoorUnits) {

            if (!unit.toolSpaceId) {

                openAccessories = false;

                break;

            }

        }

        if (!openAccessories) {

            return;

        }

        this.$refs.accordion.openAndHighlight(Section.Accessories);

    }

}
